import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useEffect, useMemo, useRef } from 'react';
import { VariableSizeList as List } from 'react-window';
import classNames from 'classnames/bind';
import { bool, func, instanceOf, node, oneOf, oneOfType, string } from 'prop-types';
import { disabledDaysPropType } from "../../../Datepicker/propsDefinition";
import Modal, { mobileHeaderTypes, mobilePositions } from "../../../Modal";
import useDisableScroll from "../../../hooks/useDisableScroll";
import { checkIsDisabledRange, formatDate, setHoursZero } from "../../../utils/date";
import { getDateByType, getIsSelectingFirstDay } from "../../../utils/date/getters";
import { getLocale } from "../../../utils/date/locale";
import { toArray } from "../../../utils/type";
import { Day, Month, Scrollbar, Week } from './components';
import { DAY_HEIGHT, LIST_HEIGHT, MONTH_TITLE_HEIGHT } from './constants';
import { getCalendarData } from './helpers';
import styles from './ScrollableCalendar.css';
var cx = classNames.bind(styles);
var modalMeta = {
  component: 'ScrollableCalendar'
};
var modalButtonProps = {
  size: 'large'
};
var CURRENT_DAY = new Date();

var ScrollableCalendar = function ScrollableCalendar(_ref) {
  var _cx;

  var disabledDays = _ref.disabledDays,
      isOpen = _ref.isOpen,
      label = _ref.label,
      locale = _ref.locale,
      maxDate = _ref.maxDate,
      minDate = _ref.minDate,
      multiple = _ref.multiple,
      onChange = _ref.onChange,
      onClose = _ref.onClose,
      onConfirm = _ref.onConfirm,
      readOnly = _ref.readOnly,
      theme = _ref.theme,
      value = _ref.value,
      withRange = _ref.withRange,
      mobilePosition = _ref.mobilePosition,
      hideCloseButton = _ref.hideCloseButton,
      headerMobile = _ref.headerMobile;
  var listRef = useRef();

  var _getLocale = getLocale(locale),
      monthNames = _getLocale.months,
      weekdaysShort = _getLocale.weekdaysShort,
      firstDayOfWeek = _getLocale.firstDayOfWeek,
      confirmLabel = _getLocale.confirmLabel;

  var _useMemo = useMemo(function () {
    return getCalendarData({
      minDate: minDate,
      maxDate: maxDate,
      monthNames: monthNames,
      firstDayOfWeek: firstDayOfWeek,
      disabledDays: disabledDays
    });
  }, [minDate, maxDate, monthNames, firstDayOfWeek, disabledDays]),
      months = _useMemo.data,
      timestampsByMonthKey = _useMemo.timestampsByMonthKey,
      disabledDaysTimestampMap = _useMemo.disabledDaysTimestampMap;

  var selectedValue = getDateByType(value, {
    multiple: multiple,
    withRange: withRange
  });
  var weekDays = [].concat(_toConsumableArray(weekdaysShort.slice(firstDayOfWeek)), _toConsumableArray(weekdaysShort.slice(0, firstDayOfWeek)));
  useEffect(function () {
    var time = setHoursZero((withRange ? selectedValue.from : selectedValue) || CURRENT_DAY);
    var listIndex = 0;
    Object.keys(timestampsByMonthKey).forEach(function (key) {
      if (timestampsByMonthKey[key].has(time)) {
        listIndex = +key;
      }
    });

    if (listIndex > 0) {
      listRef.current.scrollToItem(listIndex);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isOpen]);

  var handleClick = function handleClick(day) {
    if (readOnly) return;

    if (withRange) {
      var from = selectedValue.from,
          to = selectedValue.to;

      if (from && to && day >= from && day <= to) {
        onChange([]);
        return;
      }

      if (getIsSelectingFirstDay(from, to, day) || checkIsDisabledRange(disabledDaysTimestampMap, {
        from: from,
        to: day
      })) {
        onChange([formatDate(day), ''], false);
      } else {
        onChange([formatDate(from), formatDate(day)], true);
      }
    } else if (multiple) {
      var newDate = formatDate(day);
      var newValue = (value ? toArray(value) : []).filter(function (date) {
        return date !== newDate;
      });

      if (value.length === newValue.length) {
        newValue = [].concat(_toConsumableArray(newValue), [newDate]);
      }

      onChange(newValue, true);
    } else {
      onChange(formatDate(day), true);

      if (!onConfirm) {
        onClose();
      }
    }
  };

  var handleConfirm = function handleConfirm() {
    if (onConfirm) {
      onConfirm();
      onClose();
    }
  };

  useDisableScroll({
    isOpen: isOpen,
    onlyMobile: true,
    target: document.body
  });

  var getItemSize = function getItemSize(index) {
    return months[index].weeks.length * DAY_HEIGHT + MONTH_TITLE_HEIGHT;
  };

  return /*#__PURE__*/React.createElement(Modal, {
    buttonProps: modalButtonProps,
    closeOnOverlayClick: true,
    confirmLabel: confirmLabel,
    isOpen: isOpen,
    meta: modalMeta,
    onClose: onClose,
    onConfirm: handleConfirm,
    theme: theme,
    title: label,
    withScroll: false,
    mobilePosition: mobilePosition,
    hideCloseButton: hideCloseButton,
    headerMobile: headerMobile,
    topDivider: headerMobile === mobileHeaderTypes.back,
    withoutTopDividerGap: true
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('Wrapper', (_cx = {}, _defineProperty(_cx, "Wrapper_theme_".concat(theme), theme), _defineProperty(_cx, "selected_one_date", !selectedValue.to), _defineProperty(_cx, "range", withRange), _cx))
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('Weekdays')
  }, weekDays.map(function (day) {
    return /*#__PURE__*/React.createElement("span", {
      className: cx('Weekdays_day'),
      key: day
    }, day);
  })), /*#__PURE__*/React.createElement("div", {
    className: cx('ListContainer')
  }, /*#__PURE__*/React.createElement(List, {
    height: LIST_HEIGHT,
    itemCount: months.length,
    itemData: months,
    itemSize: getItemSize,
    outerElementType: Scrollbar,
    ref: listRef
  }, function (_ref2) {
    var data = _ref2.data,
        index = _ref2.index,
        style = _ref2.style;
    var _data$index = data[index],
        month = _data$index.month,
        year = _data$index.year,
        weeks = _data$index.weeks;
    return /*#__PURE__*/React.createElement("div", {
      className: cx('List'),
      style: _objectSpread(_objectSpread({}, style), {}, {
        padding: 0
      })
    }, /*#__PURE__*/React.createElement(Month, {
      title: month,
      yearTitle: year,
      key: "".concat(year, "_").concat(month)
    }, weeks.map(function (week, weekIndex) {
      return /*#__PURE__*/React.createElement(Week, {
        key: "".concat(year, "_").concat(month, "_").concat(weekIndex + 1)
      }, week.map(function (day, dayIndex) {
        return /*#__PURE__*/React.createElement(Day, {
          date: day.date,
          index: dayIndex,
          isDisabled: day.isDisabled,
          key: "".concat(year, "_").concat(month, "_").concat(dayIndex + 1),
          onClick: handleClick,
          multiple: multiple,
          withRange: withRange,
          month: month,
          year: year,
          selectedValue: selectedValue,
          firstDayOfWeek: firstDayOfWeek
        });
      }));
    })));
  }))));
};

ScrollableCalendar.defaultProps = {
  locale: 'ru',
  maxDate: new Date(2050, 1, 0),
  minDate: new Date(1980, 1, 0),
  theme: 'b2c'
};
export var locales = ['en', 'ru', 'it', 'es', 'de', 'zh'];
ScrollableCalendar.propTypes = process.env.NODE_ENV !== "production" ? {
  disabledDays: disabledDaysPropType,
  // eslint-disable-next-line react/boolean-prop-naming
  isOpen: bool,
  label: string,
  locale: oneOf(locales),
  maxDate: oneOfType([string, instanceOf(Date)]),
  minDate: oneOfType([string, instanceOf(Date)]),
  multiple: bool,
  onChange: func,
  onClose: func,
  onConfirm: func,
  readOnly: bool,
  theme: string,
  value: node,
  // eslint-disable-next-line react/boolean-prop-naming
  withRange: bool,
  mobilePosition: oneOf(mobilePositions),
  hideCloseButton: bool,
  headerMobile: oneOf(Object.values(mobileHeaderTypes))
} : {};
export default ScrollableCalendar;